import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Alert, Stack } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import api from '../../api';
import ElevesTable from '../../components/ElevesTable';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../PageHeader';

export default function CarnetMuscuClassDetail() {
  const { classeId } = useParams();
  const [classe, setClasse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    if (!classeId) {
      setError('Classe non fournie');
      setLoading(false);
      return;
    }

    api.get(`/classes/${classeId}`)
      .then((res) => {
        if (!mounted) return;
        setClasse(res.data);
      })
      .catch((err) => {
        console.error('Erreur chargement classe muscu', err);
        setError(err.response?.data?.error || 'Erreur lors du chargement de la classe');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [classeId]);

  if (loading) return <Box sx={{ mt: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  if (!classe) return <Alert severity="warning" sx={{ m: 4 }}>Classe introuvable.</Alert>;

  return (
    <Box sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title={classe.nom || classe.name || 'Classe'}
        subtitle="Retrouve les élèves de la classe et accède à leurs carnets de musculation."
        backTo="/apps/carnet-muscu/classes"
        homeTo="/"
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          borderRadius: { xs: 3, sm: 4 },
          boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
          background: 'linear-gradient(135deg, #fff4ea 0%, #fffaf5 100%)',
          border: '1px solid rgba(210,132,62,0.16)'
        }}
      >
        <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
          <Box
            sx={{
              width: { xs: 44, sm: 52 },
              height: { xs: 44, sm: 52 },
              borderRadius: { xs: 2.5, sm: 3 },
              background: 'linear-gradient(135deg, rgba(210,132,62,0.12) 0%, rgba(255,209,168,0.22) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
              flexShrink: 0
            }}
          >
            <GroupsRoundedIcon sx={{ fontSize: 30 }} />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                mb: 0.35,
                fontSize: { xs: '0.98rem', sm: '1.05rem' },
                lineHeight: 1.2
              }}
            >
              {classe.nom || classe.name}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.84rem', sm: '0.92rem' },
                lineHeight: 1.32,
                mb: 1.25
              }}
            >
              {classe.annee || 'Carnet de musculation de la classe'}
            </Typography>

            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/apps/carnet-muscu/settings')}
              sx={{
                textTransform: 'none',
                minHeight: 44
              }}
            >
              Paramètres du Carnet Muscu
            </Button>
          </Box>
        </Stack>
      </Paper>

      <ElevesTable classe={classe} />
    </Box>
  );
}